import type { CSSProperties, ReactNode } from "react";
import { Icon } from "./Icon";
import { useT } from "../lib/i18n";

import logoMark from "../assets/al-noor-logo-mark.png";

export function Brand() {
  const t = useT();
  return (
    <div className="brand">
      {/* The real Al Noor mark, shared with alnoortuition.com. It is navy and
          gold artwork on transparency, so it sits on a light plate — the
          placeholder's navy tile would have swallowed half of it. */}
      <div className="brand-mark">
        <img src={logoMark} alt="" width={42} height={42} />
      </div>
      <div>
        <strong>Al Noor</strong>
        <small>{t("brand.sub")}</small>
      </div>
    </div>
  );
}

export function Ring({ value, size = 68 }: { value: number; size?: number }) {
  const safe = Number.isFinite(value) ? Math.max(0, Math.min(100, Math.round(value))) : 0;
  return (
    <div
      className="progress-ring"
      role="img"
      aria-label={`${safe} percent`}
      style={{ "--p": `${safe * 3.6}deg`, width: size, height: size } as CSSProperties}
    >
      <div>
        {safe}
        <small>%</small>
      </div>
    </div>
  );
}

export function PageHeading({
  eyebrow,
  title,
  copy,
  action,
}: {
  eyebrow: string;
  title: string;
  copy: string;
  action?: ReactNode;
}) {
  return (
    <div className="page-heading">
      <div>
        <p className="eyebrow">{eyebrow}</p>
        <h1>{title}</h1>
        <p>{copy}</p>
      </div>
      {action}
    </div>
  );
}

/** An empty state that explains itself, rather than an empty grid. */
export function Empty({
  icon = "spark",
  title,
  copy,
  action,
}: {
  icon?: string;
  title: string;
  copy: string;
  action?: ReactNode;
}) {
  return (
    <div className="glass-card report-block" style={{ textAlign: "center", alignItems: "center" }}>
      <span className="icon-tile blue">
        <Icon name={icon} />
      </span>
      <h3>{title}</h3>
      <p style={{ maxWidth: "42ch", margin: "0 auto" }}>{copy}</p>
      {action}
    </div>
  );
}

export function Toast({ message }: { message: string }) {
  return (
    <div className="toast" role="status">
      <span>
        <Icon name="check" />
      </span>
      {message}
    </div>
  );
}

/** "9 August", "Yesterday", "Just now" — how a parent reads a date. */
export function friendlyDate(iso: string) {
  const value = new Date(iso);
  if (Number.isNaN(value.getTime())) return "";
  const now = new Date();
  const minutes = Math.round((now.getTime() - value.getTime()) / 60_000);
  if (minutes < 1) return "Just now";
  if (minutes < 60) return `${minutes} min ago`;

  const startOfDay = (date: Date) => new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();
  const days = Math.round((startOfDay(now) - startOfDay(value)) / 86_400_000);
  if (days === 0) return "Today";
  if (days === 1) return "Yesterday";
  return value.toLocaleDateString(undefined, { day: "numeric", month: "short" });
}

/**
 * The date, in the reader's language.
 *
 * `undefined` as the locale meant the browser's, so an Urdu page showed
 * "Sunday 9 August" in English next to Urdu prose. The app's own locale is the
 * one that matters here, not the browser's.
 */
export function longDate(date: Date, locale: string) {
  return date.toLocaleDateString(locale === "ur" ? "ur-PK" : locale, {
    weekday: "long",
    day: "numeric",
    month: "long",
  });
}
